import React, { useState, useEffect, useCallback } from 'react';
import { GitCommit } from '../../hooks/useGit';

type TimeMachineModalProps = {
  isOpen: boolean;
  filePath: string | null;
  commits: GitCommit[];
  onClose: () => void;
  onViewAtCommit: (commitId: string, filePath: string) => Promise<string | null>;
};

export const TimeMachineModal = React.memo(function TimeMachineModal({
  isOpen,
  filePath,
  commits,
  onClose,
  onViewAtCommit,
}: TimeMachineModalProps) {
  const [selectedCommit, setSelectedCommit] = useState<string | null>(null);
  const [fileContent, setFileContent] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedCommit(null);
      setFileContent(null);
      setError(null);
    }
  }, [isOpen]);

  const handleCommitSelect = useCallback(
    async (commitId: string) => {
      if (!filePath) return;

      setSelectedCommit(commitId);
      setIsLoading(true);
      setError(null);

      try {
        const content = await onViewAtCommit(commitId, filePath);
        if (content !== null) {
          setFileContent(content);
        } else {
          setError('File not found in this commit');
          setFileContent(null);
        }
      } catch (err) {
        setError('Failed to load file content');
        setFileContent(null);
      } finally {
        setIsLoading(false);
      }
    },
    [filePath, onViewAtCommit]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    },
    [onClose]
  );

  if (!isOpen || !filePath) return null;

  const fileName = filePath.split('/').pop() || filePath;

  return (
    <div className="modal-backdrop" onClick={onClose} onKeyDown={handleKeyDown}>
      <div className="modal time-machine-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">
            <span className="time-machine-icon">🕐</span>
            <span>Time Machine: {fileName}</span>
          </div>
          <button className="modal-close" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="time-machine-content">
          {/* Commit list */}
          <div className="time-machine-commits">
            <div className="time-machine-section-title">History</div>
            <div className="time-machine-commit-list">
              {commits.length === 0 ? (
                <p className="muted">No commits found</p>
              ) : (
                commits.map((commit) => (
                  <div
                    key={commit.hash}
                    className={`time-machine-commit ${selectedCommit === commit.hash ? 'selected' : ''}`}
                    onClick={() => handleCommitSelect(commit.hash)}
                  >
                    <div className="time-machine-commit-hash">{commit.hash.slice(0, 7)}</div>
                    <div className="time-machine-commit-info">
                      <div className="time-machine-commit-message">{commit.message}</div>
                      <div className="time-machine-commit-meta">
                        <span className="time-machine-commit-author">{commit.author}</span>
                        <span className="time-machine-commit-date">{commit.date}</span>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* File preview */}
          <div className="time-machine-preview">
            <div className="time-machine-section-title">
              {selectedCommit ? `Version at ${selectedCommit.slice(0, 7)}` : 'Select a commit'}
            </div>
            <div className="time-machine-preview-content">
              {isLoading ? (
                <div className="time-machine-loading">Loading...</div>
              ) : error ? (
                <div className="time-machine-error">{error}</div>
              ) : fileContent !== null ? (
                <pre className="time-machine-code">{fileContent}</pre>
              ) : (
                <div className="time-machine-placeholder">
                  Select a commit from the history to view the file at that point in time
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button className="pill-btn" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
});

export default TimeMachineModal;
